import { HireAvatarChoice } from "./HireAvatarChoice";
import { Datepicker } from "@/components/datepicker/Datepicker";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook";
import { HireStep, useHireContext } from "../hire-context";
import { HireTitleSlot } from "../hire-edit-pages";
import { getHireStepSections, HireFieldValues, isHireCompensationRequired } from "../hire-form";
import { HireCompensationLines, HireCompensationLinesHandle } from "./HireCompensationLines";
import { ApiFieldTypes } from "@/types/custom-field.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FullScreenPage, Input, Stack, Text, Title } from "@skailer-com/uikit";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { HireGradientTop } from "./HireGradientTop";
import { HireSidebar } from "./HireSidebar";

export function HireFieldsStep({ group }: { group: "main" | "personal" }) {
  const { t } = useTranslation();
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const {
    flow,
    mainValues,
    compensation,
    setCompensation,
    compensationDefaults,
    defaultCurrency,
    personalValues,
    setMainValues,
    setPersonalValues,
    employee,
    isPreview,
    isSubmitting,
    errors: serverErrors,
    steps,
    goToStep,
    personnelNumberAuto,
    onboarding,
    complete,
    exit,
  } = useHireContext();
  const step = group === "main" ? HireStep.create : HireStep.personal;
  const nextStep = steps[steps.indexOf(step) + 1];
  const previousStep = steps[steps.indexOf(step) - 1];
  const values = group === "main" ? mainValues : personalValues;
  const setValues = group === "main" ? setMainValues : setPersonalValues;
  const sections = useMemo(
    () => getHireStepSections(flow, group, personnelNumberAuto),
    [flow, group, personnelNumberAuto]
  );
  const formFields = sections
    .flatMap((section) => section.fields)
    .filter((field) => field.type !== ApiFieldTypes.employee_compensation_lines);
  const packageRef = useRef<HireCompensationLinesHandle>(null);
  const { getZodSchemaForField } = useCustomFieldsHook();
  const fieldTypes = formFields.map((field) => field.type);
  const { getEmployeeEditFieldByType } = useEmployeeCustomFieldsHook(fieldTypes);
  const requiredError = t("errors:field_required");
  const schema = z.looseObject({
    ...(group === "main"
      ? {
          first_name: z.string({ error: requiredError }).trim().min(1, requiredError),
          last_name: z.string({ error: requiredError }).trim().min(1, requiredError),
          middle_name: z.string().nullish(),
          date_hired_on: z.date({ error: requiredError }),
        }
      : {}),
    ...Object.assign(
      {},
      ...formFields.map((field) =>
        getZodSchemaForField(
          field.type === ApiFieldTypes.employee_number && personnelNumberAuto === undefined
            ? { ...field, is_mandatory: false }
            : field
        )
      )
    ),
  });
  const form = useForm<HireFieldValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema) as unknown as Resolver<HireFieldValues>,
    defaultValues: values,
  });
  const {
    register,
    control,
    handleSubmit,
    subscribe,
    setError,
    formState: { errors },
  } = form;
  useEffect(() => {
    return subscribe({ formState: { values: true }, callback: ({ values }) => setValues(values) });
  }, [subscribe, setValues]);
  useEffect(() => {
    for (const [name, message] of Object.entries(serverErrors[step] ?? {}))
      setError(name, { type: "server", message });
  }, [serverErrors, step, setError]);
  const fieldErrorKeys = new Set([
    ...formFields.map((field) => field.slug),
    "first_name",
    "last_name",
    "middle_name",
    "date_hired_on",
    "compensation",
  ]);
  const generalErrors = Object.entries(serverErrors[step] ?? {}).filter(
    ([key]) => !fieldErrorKeys.has(key)
  );
  const submit = async () => {
    if (isSubmitting) return;
    const isPackageValid = (await packageRef.current?.validate()) ?? true;
    await handleSubmit(async (data) => {
      if (!isPackageValid) return;
      setValues(data);
      if (nextStep) goToStep(nextStep);
      else await complete(onboarding, { [group]: data });
    })();
  };
  const nextButton = (
    <Button
      size="lg"
      onClick={() => void submit()}
      disabled={uploadingAvatar}
      isLoading={!nextStep && isSubmitting}
    >
      {t(nextStep ? "common:continue" : "common:complete")}
    </Button>
  );
  return (
    <FullScreenPage
      isCenterVertical={false}
      backSlot={
        <Button size="lg" variant="secondary" onClick={exit} disabled={isSubmitting}>
          {t(isPreview ? "common:close" : "common:exit")}
        </Button>
      }
      actionsSlot={
        <Stack direction="horizontal" gap="sm">
          {previousStep && (
            <Button
              size="lg"
              variant="secondary"
              disabled={isSubmitting}
              onClick={() => goToStep(previousStep)}
            >
              {t("common:back")}
            </Button>
          )}
          {nextButton}
        </Stack>
      }
      titleSlot={<HireTitleSlot step={step} employee={employee} />}
      backgroundSlot={<HireGradientTop />}
      sidebarSlot={steps.length > 1 ? <HireSidebar step={step} /> : undefined}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack gap="sm">
          {isPreview && <Text className="text-secondary">{t("hiring_flow:preview_hint")}</Text>}
          {group === "main" && (
            <>
              <Stack className="card-panel" gap="lg">
                <Title
                  header={t("core:add_new_teammate")}
                  caption={t("core:your_team_growing")}
                  size="lg"
                />
                <HireAvatarChoice
                  form={form}
                  preview={isPreview}
                  onUploadingChange={setUploadingAvatar}
                />
                <Stack>
                  <Input
                    required
                    label={t("core:first_name")}
                    {...register("first_name")}
                    error={errors.first_name?.message as string | undefined}
                  />
                  <Input
                    required
                    label={t("core:last_name")}
                    {...register("last_name")}
                    error={errors.last_name?.message as string | undefined}
                  />
                  <Input
                    label={t("core:middle_name")}
                    {...register("middle_name")}
                    error={errors.middle_name?.message as string | undefined}
                  />
                </Stack>
              </Stack>
              <Stack className="card-panel" gap="lg">
                <Title
                  header={t("core:hire_date")}
                  caption={t("core:choose_when_employee_start")}
                />
                <Controller
                  name="date_hired_on"
                  control={control}
                  render={({ field }) => (
                    <Datepicker
                      required
                      label={t("core:choose_hire_date")}
                      value={field.value as Date}
                      onChange={field.onChange}
                      error={errors.date_hired_on?.message as string | undefined}
                    />
                  )}
                />
              </Stack>
            </>
          )}
          {sections.map((section, index) => (
            <Stack key={`${section.section_type}-${index}`} className="card-panel" gap="lg">
              <Title header={section.title || t(`hiring_flow:section_${section.section_type}`)} />
              <Stack>
                {section.fields.map((field) => (
                  <div key={field.slug}>
                    {field.type === ApiFieldTypes.employee_compensation_lines ? (
                      <HireCompensationLines
                        ref={packageRef}
                        value={compensation}
                        onChange={setCompensation}
                        required={isHireCompensationRequired(flow)}
                        basicSalaryDefaults={compensationDefaults}
                        defaultCurrency={defaultCurrency}
                        serverError={serverErrors[step]?.compensation}
                      />
                    ) : (
                      employee &&
                      getEmployeeEditFieldByType(field, employee, form, {
                        forceCanView: true,
                        forceCanEdit: true,
                      })
                    )}
                  </div>
                ))}
              </Stack>
            </Stack>
          ))}
          {generalErrors.map(([key, message]) => (
            <Text key={key} className="text-danger">
              {message}
            </Text>
          ))}
          {nextButton}
        </Stack>
      </form>
    </FullScreenPage>
  );
}
